import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { useUserOptions } from '@/lib/use-user-options';
import { BILLING_REGIONS, useBillingRegionLabels } from '@/lib/billing-region';
import { denialReason, type Ability } from '@/lib/permissions';
import type {
  EnquiryAlertBand,
  EnquiryAlertRecipient,
  EnquiryAlertRecipients,
} from '@/lib/types';
import {
  Button,
  Field,
  MultiCombobox,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  type MultiComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * WHO AT SEED GETS AN IN-APP POPUP WHEN A NEW ENQUIRY ARRIVES, PER BILLING
 * REGION.
 *
 * Read and written through `GET | PUT /settings/enquiry-alert-recipients`.
 * Copied in structure from `B2bProposalRecipientsCard` — loading skeleton, a
 * `403` rendered as a refusal with no controls, failure and refusal split on the
 * STATUS, save re-seeded from the response — with one real difference: this
 * setting holds USER IDS, not addresses, so each region is a staff picker rather
 * than a list of text boxes.
 *
 * ## Admin tier on the READ as well as the write
 *
 * A non-admin gets a `403` on the GET, rendered as a refusal about their
 * account — never as an empty set of regions, which would state that nobody is
 * alerted, a claim about the business rather than about their access.
 *
 * ## ⛔ The write is a WHOLE-SET REPLACE across all four regions
 *
 * The server requires every region key (`[]` clears one), so the body always
 * carries all four, built from the draft. There is no omitted case and nothing
 * here may send a partial body.
 *
 * ## The region names are FETCHED
 *
 * Rows follow `BILLING_REGIONS` (the master's order) and are labelled through
 * `useBillingRegionLabels`, never a literal map — the names are admin-editable
 * columns and a hardcoded copy goes silently stale after the first rename. A
 * failed label read falls back to the slug, which is visible and deliberate.
 */

/**
 * How many people one region may alert.
 *
 * ⚠️ **READ FROM `ENQUIRY_ALERT_RECIPIENTS_PER_REGION_MAX` in the backend's
 * `settings.schema.ts`, not chosen here.** An equal value to the sibling caps on
 * this page is a coincidence, not a relationship.
 */
const MAX_PER_REGION = 25;

type Draft = Record<EnquiryAlertBand, string[]>;

const REGIONS = BILLING_REGIONS as readonly EnquiryAlertBand[];

function emptyDraft(): Draft {
  return { united_kingdom: [], europe: [], north_america: [], rest_of_world: [] };
}

function draftFrom(settings: EnquiryAlertRecipients): Draft {
  const draft = emptyDraft();
  for (const band of REGIONS) {
    draft[band] = (settings.regions[band] ?? []).map((person) => String(person.id));
  }
  return draft;
}

/** Order-independent: the server may serve a set in a different order than it was ticked. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((value) => seen.has(value));
}

/**
 * The sections a person cannot read, as the enquiry tabs call them.
 *
 * ⚠️ **This is the one sentence on the card beyond the intro, and it is allowed
 * because it names a consequence the reader cannot otherwise see**: the alert is
 * filtered by the recipient's own read grant, so somebody chosen here may still
 * never get a popup for a section. Nothing else on screen would say so.
 */
function unreadableSections(person: EnquiryAlertRecipient): string[] {
  const sections: string[] = [];
  if (!person.canRead.eventInterest) sections.push('event interest');
  if (!person.canRead.b2bProposal) sections.push('B2B proposals');
  if (!person.canRead.reportDownload) sections.push('report leads');
  return sections;
}

function joinSections(sections: readonly string[]): string {
  if (sections.length <= 1) return sections.join('');
  return `${sections.slice(0, -1).join(', ')} or ${sections[sections.length - 1]}`;
}

function personLabel(person: { name: string; email: string; id: number }): string {
  return person.name.trim() || person.email.trim() || `Staff #${person.id}`;
}


/** Same order as the sibling cards: the admin tier is the floor, the permission narrows it. */
function recipientsDenialFor(ability: Ability): string | null {
  // 2026-10-01: the set decides for every role. *SUPERSEDED: admin tier first.*
  // 2026-10-02 (owner): recipients and routing are `settings-notifications`.
  return denialReason(ability, 'update', 'settings-notifications');
}

export function EnquiryAlertRecipientsCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const regionLabel = useBillingRegionLabels();

  const [settings, setSettings] = useState<EnquiryAlertRecipients | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  /** Per-REGION messages, from `regions.<band>` or `regions.<band>.<n>`. */
  const [regionErrors, setRegionErrors] = useState<Partial<Record<EnquiryAlertBand, string>>>({});
  /** The ids a server refused, per region — marks the chips rather than the whole box. */
  const [refusedIds, setRefusedIds] = useState<Partial<Record<EnquiryAlertBand, string[]>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  /*
   * The whole staff directory — deliberately NOT narrowed to a user group. Only
   * fetched once the read has come back allowed, so a refused reader issues no
   * directory request for a picker they will never see.
   */
  const staff = useUserOptions(settings !== null);

  const load = useCallback((signal: AbortSignal) => {
    return api
      .getEnquiryAlertRecipients(signal)
      .then((response) => {
        if (signal.aborted) return;
        setSettings(response.data);
        setDraft(draftFrom(response.data));
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        // A 403 is a refusal about THIS reader, never a failure of the API.
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        // A 401 is left to `@/lib/api`, which refreshes or ends the session.
        if (caught instanceof ApiError && caught.status === 401) return;
        setError({
          title: 'Couldn’t read the enquiry popup recipients.',
          message:
            caught instanceof Error
              ? caught.message
              : 'The list could not be read, so what is on screen may not be what is stored.',
        });
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const denial = recipientsDenialFor(ability);

  /*
   * Stored recipients are injected into the options, so a chosen person renders
   * by name even while the directory is loading, failed, or truncated past them.
   */
  const storedPeople = useMemo(() => {
    const byId = new Map<string, EnquiryAlertRecipient>();
    if (settings) {
      for (const band of REGIONS) {
        for (const person of settings.regions[band] ?? []) byId.set(String(person.id), person);
      }
    }
    return byId;
  }, [settings]);

  const options = useMemo<MultiComboboxOption[]>(() => {
    const seen = new Set<string>();
    const result: MultiComboboxOption[] = [];
    for (const user of staff.users) {
      const value = String(user.id);
      seen.add(value);
      result.push({ value, label: personLabel(user), hint: user.email });
    }
    for (const [value, person] of storedPeople) {
      if (seen.has(value)) continue;
      result.push({ value, label: personLabel(person), hint: person.email });
    }
    return result;
  }, [staff.users, storedPeople]);

  const dirty =
    settings !== null &&
    REGIONS.some((band) => !sameSet(draft[band], draftFrom(settings)[band]));

  function applySaved(next: EnquiryAlertRecipients) {
    setSettings(next);
    // Re-seeded from the RESPONSE: the server de-duplicates, and the `canRead`
    // flags that drive the warnings are only ever the server's answer.
    setDraft(draftFrom(next));
    setRegionErrors({});
    setRefusedIds({});
    setSaved(true);
  }

  function handleWriteFailure(caught: unknown, sent: Record<EnquiryAlertBand, number[]>) {
    const title = 'Couldn’t save the enquiry popup recipients.';
    if (!(caught instanceof ApiError)) {
      setError({
        title,
        message: caught instanceof Error ? caught.message : 'The list was not saved.',
      });
      return;
    }
    /*
     * Claims `regions.<band>` for the whole array and one `regions.<band>.<n>`
     * per id ACTUALLY SENT, so the index the server names is an index into the
     * body — never into the draft.
     */
    const keys: string[] = [];
    for (const band of REGIONS) {
      keys.push(`regions.${band}`);
      sent[band].forEach((_id, index) => keys.push(`regions.${band}.${index}`));
    }
    const routed = caught.routeFieldErrors(keys);
    const byRegion: Partial<Record<EnquiryAlertBand, string>> = {};
    const refused: Partial<Record<EnquiryAlertBand, string[]>> = {};
    const unroutable = [...routed.unroutable];

    for (const [path, message] of Object.entries(routed.fields)) {
      const [, band, index] = path.split('.') as [string, EnquiryAlertBand, string | undefined];
      if (!REGIONS.includes(band)) {
        unroutable.push(`${path}: ${message}`);
        continue;
      }
      byRegion[band] = byRegion[band] ? `${byRegion[band]} ${message}` : message;
      if (index !== undefined) {
        const id = sent[band][Number(index)];
        if (id !== undefined) refused[band] = [...(refused[band] ?? []), String(id)];
      }
    }

    setRegionErrors(byRegion);
    setRefusedIds(refused);
    if (unroutable.length > 0) {
      setError({ title, message: unroutable.join(' ') });
    } else if (Object.keys(byRegion).length === 0) {
      // A 403 carries no `details`, so there is nothing to route.
      setError({ title, message: caught.message });
    }
  }

  function save() {
    if (saving || !dirty) return;
    const sent = {} as Record<EnquiryAlertBand, number[]>;
    for (const band of REGIONS) sent[band] = draft[band].map(Number);
    setSaving(true);
    setSaved(false);
    setError(null);
    setRegionErrors({});
    setRefusedIds({});
    api
      .setEnquiryAlertRecipients({ regions: sent })
      .then((response) => applySaved(response.data))
      .catch((caught: unknown) => handleWriteFailure(caught, sent))
      .finally(() => setSaving(false));
  }

  function updateRegion(band: EnquiryAlertBand, values: string[]) {
    setSaved(false);
    setRegionErrors((current) => {
      if (!(band in current)) return current;
      const next = { ...current };
      delete next[band];
      return next;
    });
    setRefusedIds((current) => {
      if (!(band in current)) return current;
      const next = { ...current };
      delete next[band];
      return next;
    });
    setDraft((current) => ({ ...current, [band]: values }));
  }

  const saveDisabled = denial !== null || saving || !dirty;
  // The unchanged reason is withheld right after a save, where "Saved." already
  // says why the button is off and a second sentence beside it reads as a fault.
  const saveReason =
    denial ?? (!dirty && settings !== null && !saved ? 'Nothing has changed.' : null);

  const staffFootnote = staff.failed
    ? 'The staff list could not be read.'
    : staff.hasMore
      ? 'Showing the first page of staff.'
      : undefined;

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Enquiry popups" />
      <PanelBody className="flex flex-col gap-4">
        {error && (
          <Notice tone="danger" role="alert" title={error.title}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error.message}</span>
          </Notice>
        )}

        {loading && settings === null && !forbidden ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">
            Your permission set does not include this. An administrator can change that.
          </p>
        ) : settings === null ? null : (
          <>
            <p className="m-0 text-xs text-fg-subtle">
              Chosen people get an in-app popup, chime and desktop notification when a new enquiry
              arrives from their region. Enquiries with no known region go to everyone listed.
            </p>

            <div className="flex flex-col gap-4">
              {REGIONS.map((band) => {
                const label = regionLabel(band);
                /*
                 * Warnings read the SERVER's `canRead` for people it has stored.
                 * Somebody ticked but not yet saved has no answer, so no line —
                 * a guess here would be a claim about a grant nobody checked.
                 */
                const warnings = draft[band]
                  .map((value) => storedPeople.get(value))
                  .filter((person): person is EnquiryAlertRecipient => person !== undefined)
                  .map((person) => ({ person, sections: unreadableSections(person) }))
                  .filter((entry) => entry.sections.length > 0);
                return (
                  <div key={band} className="flex flex-col gap-1.5">
                    <Field label={label} error={regionErrors[band]}>
                      {({ id, 'aria-describedby': describedBy }) => (
                          <MultiCombobox
                            id={id}
                            aria-describedby={describedBy}
                            values={draft[band]}
                            onChange={(values) => updateRegion(band, values)}
                            options={options}
                            placeholder="Nobody chosen"
                            searchPlaceholder="Search staff…"
                            loading={staff.loading}
                            disabled={denial !== null || saving}
                            invalid={Boolean(regionErrors[band])}
                            invalidValues={refusedIds[band]}
                            maxSelected={MAX_PER_REGION}
                            summaryNoun={{ singular: 'person', plural: 'people' }}
                            {...(staffFootnote ? { footnote: staffFootnote } : {})}
                          />
                      )}
                    </Field>
                    {warnings.map(({ person, sections }) => (
                      <p key={person.id} className="m-0 text-xs text-warning">
                        {`${personLabel(person)} can’t read ${joinSections(sections)}, so won’t get those popups.`}
                      </p>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              {/* The `title` is on a WRAPPER: a disabled `Button` never receives the hover. */}
              <span title={saveReason ?? undefined}>
                <Button onClick={save} disabled={saveDisabled}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </span>
              {saveReason && <p className="m-0 text-xs text-fg-subtle">{saveReason}</p>}
              {saved && !denial && (
                <p role="status" className="m-0 text-xs text-fg-subtle">
                  Saved.
                </p>
              )}
            </div>
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
